import { Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Box,
  IconButton,
  Menu,
  Toolbar,
  useTheme,
} from "@mui/material";
import { AppNavBreadcrumbs } from "../AppNavBreadcrumbs";
import { ChangeLogButton } from "../changelog";
import { UserMenu } from "../user-menu";
import { COLOR_MODE_OPTIONS, useColorMode } from "~/providers/color-mode";
import { useAppNavDrawer } from "~/providers/app-nav-drawer";
import {
  DarkMode as DarkModeIcon,
  GitHub as GitHubIcon,
  LightMode as LightModeIcon,
  MenuBook as MenuBookIcon,
} from "@mui/icons-material";
import { SettingsBrightness } from "@mui/icons-material";
import { Config } from "~/constants/configuration_provider";
import { NavbarTooltip } from "../navbar-tooltip";
import { IncidentBanner } from "~/components/IncidentBanner/IncidentBanner";
import {
  INCIDENT_BANNER_HEIGHT,
  IncidentIndicator,
} from "~/components/IncidentBanner/useIncidentBanner";
import { useAuth } from "~/login/provider";
import { FeatureFlagsButton } from "~/lib/FeatureFlags/FeatureFlagsButton";
import { SlackBwIcon } from "~/icons";
import { useEffect, useMemo, useState } from "react";
import { MondooLogoHorizontal } from "~/components/MondooLogoHorizontal/MondooLogoHorizontal";
import { AppSearch } from "~/components/AppNavToolbar/AppSearch/AppSearch";
import NextConsoleAnnouncementBanner, {
  NEXT_CONSOLE_ANNOUNCEMENT_BANNER_DEFAULT_HEIGHT,
  isMondooHostedInstance,
  isNextConsoleAnnouncementDismissed,
} from "../NextConsoleAnnouncementBanner/NextConsoleAnnouncementBanner";

export function AppNavToolbar() {
  const { colorMode, setColorMode } = useColorMode();
  const navDrawer = useAppNavDrawer();
  const { incident } = useAuth();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const colorModeMenuOpen = Boolean(anchorEl);

  const orderedColorModeOptions = useMemo(() => {
    const arr = [...COLOR_MODE_OPTIONS];
    arr.sort((a, b) =>
      a.value === colorMode ? 1 : b.value === colorMode ? -1 : 0,
    );
    return arr;
  }, [colorMode]);

  const handleOpenColorModeMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleCloseColorModeMenu = () => setAnchorEl(null);

  const [isAnnouncementDismissed, setIsAnnouncementDismissed] = useState(() =>
    isNextConsoleAnnouncementDismissed(),
  );
  const [announcementBannerHeight, setAnnouncementBannerHeight] = useState(
    NEXT_CONSOLE_ANNOUNCEMENT_BANNER_DEFAULT_HEIGHT,
  );
  const isMondooHosted = useMemo(() => isMondooHostedInstance(), []);
  const isAnnouncementBannerShown = isMondooHosted && !isAnnouncementDismissed;

  const isIncidentBannerShown =
    incident?.data?.indicator !== IncidentIndicator.NONE &&
    incident?.showIncidentBanner;

  const incidentBannerOffset = isIncidentBannerShown
    ? INCIDENT_BANNER_HEIGHT
    : 0;
  const announcementBannerOffset = isAnnouncementBannerShown
    ? announcementBannerHeight
    : 0;
  const appBarTop = incidentBannerOffset + announcementBannerOffset;

  // Publish the announcement-banner offset so the side nav drawer (which
  // is fixed-positioned and lives outside this component's flow) can sit
  // below the banner instead of being covered by it.
  const { setBannerOffset } = navDrawer;
  useEffect(() => {
    setBannerOffset(announcementBannerOffset);
    return () => setBannerOffset(0);
  }, [announcementBannerOffset, setBannerOffset]);

  return (
    <>
      <IncidentBanner />
      {isAnnouncementBannerShown && (
        <NextConsoleAnnouncementBanner
          top={incidentBannerOffset}
          onDismiss={() => setIsAnnouncementDismissed(true)}
          onHeightChange={setAnnouncementBannerHeight}
        />
      )}
      <Box sx={{ height: navDrawer.navbarHeight + appBarTop }}>
        <AppBar
          position="fixed"
          enableColorOnDark
          elevation={0}
          sx={{
            top: appBarTop ? `${appBarTop}px` : 0,
            borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "primary.darker",
          }}
        >
          <Toolbar disableGutters>
            <Box
              component={RouterLink}
              id="toolbar-logo"
              to="/dashboard"
              title="Mondoo Dashboard"
              data-name="nav-mondoo-logo"
              sx={{
                display: "flex",
                pl: 2,
                width: navDrawer.width,
                height: (theme) => theme.spacing(8),
                borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                transition: (theme) =>
                  theme.transitions.create("width", navDrawer.transition),
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <MondooLogoHorizontal
                layout={{
                  flexShrink: 0,
                  width: "112px",
                  transition: (theme) =>
                    theme.transitions.create("opacity", navDrawer.transition),
                  "#mondoo-logo-text": {
                    transition: (theme) =>
                      theme.transitions.create("opacity", navDrawer.transition),
                  },
                }}
                iconOnly={!navDrawer.open}
                color={theme.palette.common.white}
              />
            </Box>

            <AppNavBreadcrumbs />

            <FeatureFlagsButton />
            <NavbarTooltip title="Color Mode">
              <IconButton
                data-name="nav-color-mode"
                color="inherit"
                size="large"
                onClick={handleOpenColorModeMenu}
                aria-label="Color Mode"
              >
                {colorMode === "system" && <SettingsBrightness />}

                {colorMode === "light" && <LightModeIcon />}

                {colorMode === "dark" && <DarkModeIcon />}
              </IconButton>
            </NavbarTooltip>
            <Menu
              anchorEl={anchorEl}
              open={colorModeMenuOpen}
              onClose={handleCloseColorModeMenu}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                list: {
                  sx: { p: 0, bgcolor: "transparent" },
                },
                paper: {
                  elevation: 0,
                  sx: {
                    bgcolor: "transparent",
                    boxShadow: "none",
                    p: 0,
                    overflow: "visible",
                    ml: "44px",
                    mt: "-4px",
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                {orderedColorModeOptions.map(({ value, icon, label }) => (
                  <NavbarTooltip key={value} title={label}>
                    <IconButton
                      onClick={async () => {
                        await setColorMode(value);
                        handleCloseColorModeMenu();
                      }}
                      aria-label={label}
                      sx={{
                        borderRadius: "50%",
                        width: 40,
                        height: 40,
                        bgcolor:
                          colorMode === value
                            ? "primary.main"
                            : "background.paper",
                        color:
                          colorMode === value
                            ? "primary.contrastText"
                            : "text.primary",
                        boxShadow: 1,
                        zIndex: 1,
                        "&:hover": {
                          bgcolor:
                            colorMode === value
                              ? "primary.main"
                              : "background.lightest",
                          boxShadow: 2,
                        },
                        "&:focus-visible": {
                          outline: "none",
                          boxShadow: 2,
                        },
                      }}
                    >
                      {icon}
                    </IconButton>
                  </NavbarTooltip>
                ))}
              </Box>
            </Menu>

            <NavbarTooltip title="Documentation">
              <IconButton
                data-name="nav-docs-button"
                color="inherit"
                size="large"
                href={"https://mondoo.com/docs"}
                target="_blank"
                aria-label="Documentation"
              >
                <MenuBookIcon />
              </IconButton>
            </NavbarTooltip>
            <NavbarTooltip title="Mondoo GitHub">
              <IconButton
                data-name="nav-github-button"
                color="inherit"
                size="large"
                href={"https://github.com/mondoohq/"}
                target="_blank"
                aria-label="Mondoo GitHub"
              >
                <GitHubIcon />
              </IconButton>
            </NavbarTooltip>
            {Config.VITE_HIDE_COMMUNITY_ICON !== "true" && (
              <NavbarTooltip title="Mondoo Community">
                <IconButton
                  data-name="nav-slack-button"
                  color="inherit"
                  size="large"
                  href={`${Config.VITE_COMMUNITY_INVITE_LINK}`}
                  target="_blank"
                  aria-label="Mondoo Community"
                >
                  <SlackBwIcon />
                </IconButton>
              </NavbarTooltip>
            )}
            <ChangeLogButton />
            <UserMenu />
            <AppSearch />
          </Toolbar>
        </AppBar>
      </Box>
    </>
  );
}
